import { Component, effect, HostListener, inject, viewChild, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Listbox, ListboxChangeEvent } from '@openng/optimus-ui/listbox';
import { WorkGridItemObject } from '@it2go/types';
import {
    InputFulltextComponent,
    IOption,
} from '../../../../shared/components/inputs/input-fulltext/input-fulltext.component';
import { FolderServiceRecordsPageStore } from './folder-service-records-page.store';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { ContextMenu } from '@openng/optimus-ui/contextmenu';
import { MenuItem, PrimeTemplate } from '@openng/optimus-ui/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { WorkDataServiceRecordObject } from '@libs/types/src/services/object-type/work/work-service-record.object';
import { fb, isAllowed } from '../../../../shared/shared.global-service';
import { isInvalid } from '../../../../shared/helpers/validate';
import { contentGlobalSuccess } from '../../../content.error';
import { FilterBuilder } from '../../../helper/filter.builder';
import { ServicePatternStructureUsageEnum } from '@libs/types/src/services/enum/service-pattern-structure-usage.enum';
import { formAddText } from '../../../../../utils/form.utils';
import {
    ServicePatternWysiwygComponent,
} from '../../../components/common/service-pattern-wysiwyg/service-pattern-wysiwyg.component';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { UserChipComponent } from '../../../../../components/kit/user-chip/user-chip.component';
import { FormsModule } from '@angular/forms';
import { wysiwygPatterGroupsMedicalFolder } from '@libs/types/src/common/enum/wysiwyg-placeholder.enum';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import { WorkPrintService } from '../../../../../components/medical-service/work/work-print.service';

@Component({
    selector: 'app-folder-service-records-page',
    templateUrl: './folder-service-records-page.component.html',
    styleUrls: ['./folder-service-records-page.component.scss'],
    providers: [FolderServiceRecordsPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        InputFulltextComponent,
        ButtonComponent,
        ServicePatternWysiwygComponent,
        Listbox,
        TranslatePipe,
        PrimeTemplate,
        UserChipComponent,
        ContextMenu,
        FormsModule,
    ],
})
export class FolderServiceRecordsPageComponent extends SubscriptionComponent {

    protected readonly store = inject(FolderServiceRecordsPageStore);
    private readonly translate = inject(TranslateService);
    private readonly workPrint = inject(WorkPrintService);

    protected selectedServiceRecord: WorkGridItemObject<WorkDataServiceRecordObject> | null = null;
    protected readonly folderStore = inject(FolderGlobalStore);

    public readonly wysiwyg = viewChild<ServicePatternWysiwygComponent>(ServicePatternWysiwygComponent);

    protected form = fb.group({
        search: fb.control<string>(''),
        selectedServiceRecordData: fb.control<string>('', { nonNullable: true }),
    });

    @ViewChild('contextMenu') ctx!: ContextMenu;
    protected contextMenuItemsAll: MenuItem[] = [
        {
            label: 'update', icon: 'pi pi-file-edit', command: () => {
                this.update();
            },
        },
        {
            label: 'delete', icon: 'pi pi-times', command: () => {
                this.delete();
            },
        },
    ];
    protected contextMenuItems = this.contextMenuItemsAll;
    private contextItem: number = 0;

    constructor() {
        super();

        effect(() => {
            if (!isAllowed['folderRecord.create']()) {
                this.form.disable();
            } else {
                this.form.enable();
            }
        });

        this.store.onSuccess(
            () => this.fetchData(),
            'createWork',
            'updateWork',
            'deleteWork',
        );

        effect(() => {
            const serviceRecords = this.store.serviceRecords();

            if (serviceRecords.length > 0) {
                this.selectedServiceRecord = serviceRecords[0];
                if (this.selectedServiceRecord) {
                    this.form.patchValue({
                        selectedServiceRecordData: this.selectedServiceRecord['workData']?.['note'] || '',
                    });
                } else {
                    this.form.reset();
                }
            }
        });

        effect(() => {
            if (this.store.folder()) {
                this.fetchData();
            }
        });

        this.contextMenuItemsAll.forEach((it) => {
            it.label = this.translate.instant(`database.persons.serviceRecord.${it.label}`);
        });
    }

    public listChange(index: ListboxChangeEvent): void {
        if (index.value) {
            this.selectedServiceRecord = this.store.serviceRecords()[index.value.index];
            this.form.patchValue({
                selectedServiceRecordData: this.store.serviceRecords()[index.value.index]['workData']?.['note'] || '',
            });
        }
    }

    @HostListener('document:contextmenu')
    public rightClickAny() {
        this.ctx.hide();
    }

    @HostListener('document:click')
    public leftClickAny() {
        this.ctx.hide();
    }

    public rightClick(menu: ContextMenu, event: MouseEvent, item: any) {
        this.contextItem = item.id;
        menu.hide();
        event.preventDefault();
        this.contextMenuItems = this.contextMenuItemsAll.slice(this.selectedServiceRecord?.id === item.id ? 0 : 1);
        setTimeout(() => {
            menu.toggle(event);
        }, 1);
    }

    public async save() {
        if (isInvalid(this.form)) return;

        if (!this.store.folder()) return;
        await this.store.api.createWork({
            folderId: this.store.folder()?.id || 0,
            dateFrom: new Date().toISOString(),
            workType: WorkTypeEnum.SERVICE_RECORD,
            userIds: [this.store.globalGlobalStore.loggedUserStore.currentUserId() || ''],
            workData: JSON.stringify({
                note: this.form.value.selectedServiceRecordData || '',
            }),
        });

        this.wysiwyg()?.resetBackupValues();

        contentGlobalSuccess.next('common.created');
    }

    protected selectedServiceRecordChange(selectedServiceRecordSearch: IOption | null): void {
        this.selectedServiceRecord = selectedServiceRecordSearch as any;
        this.form.patchValue({ selectedServiceRecordData: this.selectedServiceRecord?.workData?.note ?? '' });
    }

    protected async delete() {
        if (this.contextItem) {
            await this.store.api.deleteWork({ id: this.contextItem });
            contentGlobalSuccess.next('common.deleted');
        }
    }

    protected async update() {
        if (this.contextItem) {
            await this.store.api.updateWork({
                id: this.contextItem,
                workData: JSON.stringify({
                    note: this.form.value.selectedServiceRecordData || '',
                }),
            });

            this.wysiwyg()?.resetBackupValues();

            contentGlobalSuccess.next('common.updated');
        }
    }

    protected print(): void {
        if (this.selectedServiceRecord) {
            this.workPrint.openRow(this.selectedServiceRecord);
        }
    }

    private fetchData() {
        if (!this.store.folderId()) return;
        this.store.api.getWorks(FilterBuilder.singlePage({
            folderId: this.store.folderId(),
            workType: WorkTypeEnum.SERVICE_RECORD,
            sort: { column: 'dateFrom', direction: 'DESC' },
        })).then();
    }

    protected readonly isAllowed = isAllowed;
    protected readonly ServicePatternStructureUsageEnum = ServicePatternStructureUsageEnum;
    protected readonly formAddText = formAddText;
    protected readonly wysiwygPatterGroupsMedicalFolder = wysiwygPatterGroupsMedicalFolder;
}
